import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { oclear as clearOfflineStore } from '../lib/offlineStore'

const API          = import.meta.env.VITE_API_URL || '/api'
const TOKEN_KEY    = 'polar_token'
const USER_KEY     = 'polar_user'
const CHECK_MS     = 5 * 60 * 1000   // verify session every 5 min

// Dentro de la app nativa: el caparazón guarda la sesión en el almacenamiento
// seguro del teléfono (el storage del WebView puede no sobrevivir reinicios) y
// la reinyecta al arrancar. En navegador normal esto no hace nada.
const postSessionToShell = (token, user) => {
  try { window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'auth:token', token, user })) } catch {}
}

export function useAuth() {
  const navigate = useNavigate()
  const [token, setToken]   = useState(() => localStorage.getItem(TOKEN_KEY))
  const [user,  setUser]    = useState(() => { try { return JSON.parse(localStorage.getItem(USER_KEY)) } catch { return null } })
  const [error, setError]   = useState(null)
  const [loading, setLoading] = useState(false)
  const intervalRef = useRef(null)

  const logout = useCallback((redirect = true) => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    postSessionToShell(null, null)   // borra la sesión guardada en el teléfono
    // Borra los datos de la última semana guardados para el modo offline: si
    // otra persona inicia sesión en este teléfono no debe ver datos ajenos.
    try { window.caches?.delete('manabi-api-v1'); window.caches?.delete('vitalcore-api-v1') } catch {}
    try { clearOfflineStore() } catch {}
    try { localStorage.removeItem('vc_warm_ts') } catch {}
    setToken(null)
    setUser(null)
    if (redirect) navigate('/login')
  }, [navigate])

  const login = useCallback(async (email, password) => {
    setLoading(true)
    setError(null)
    try {
      const res  = await fetch(`${API}/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      localStorage.setItem(TOKEN_KEY, data.token)
      localStorage.setItem(USER_KEY, JSON.stringify(data.user))
      if (data.user?.polar_device_id || data.user?.email === 'matias@cagliero.ar') {
        try { localStorage.setItem('vc_polar_id', data.user?.polar_device_id || '18930C3E') } catch {}
      }
      if (data.user?.polar_device_name || data.user?.email === 'matias@cagliero.ar') {
        try { localStorage.setItem('vc_polar_name', data.user?.polar_device_name || 'Polar H10 18930C3E') } catch {}
      }
      setToken(data.token)
      setUser(data.user)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Verify token with server; logout if invalid/expired
  const verifySession = useCallback(async (tok) => {
    if (!tok) return
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${tok}` },
      })
      if (res.status === 401 || res.status === 403 || res.status === 404) {
        // 401/403 = invalid/expired token; 404 = demo token (no real account in DB)
        logout(true)
        return
      }
      if (res.ok) {
        const data = await res.json()
        setUser(data)
        localStorage.setItem(USER_KEY, JSON.stringify(data))
        if (data.polar_device_id || data.email === 'matias@cagliero.ar') {
          try { localStorage.setItem('vc_polar_id', data.polar_device_id || '18930C3E') } catch {}
        }
        if (data.polar_device_name || data.email === 'matias@cagliero.ar') {
          try { localStorage.setItem('vc_polar_name', data.polar_device_name || 'Polar H10 18930C3E') } catch {}
        }
      }
    } catch {
      // network error — keep session alive, don't log out
    }
  }, [logout])

  useEffect(() => {
    if (!token) return

    // Sesión nueva o restaurada → el caparazón nativo la persiste en el teléfono
    // (cubre login, registro y cambio de token: todos pasan por este estado).
    try { postSessionToShell(token, JSON.parse(localStorage.getItem(USER_KEY) || 'null')) } catch {}

    // Immediate check on mount
    verifySession(token)

    // Periodic check
    intervalRef.current = setInterval(() => verifySession(token), CHECK_MS)

    // Check when tab becomes visible again (catches expired sessions after being idle)
    const onVisible = () => { if (document.visibilityState === 'visible') verifySession(token) }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(intervalRef.current)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [token, verifySession])

  useEffect(() => {
    const handleAvatarUpdated = (e) => {
      const avatar = e.detail?.avatar ?? null
      setUser(prev => {
        if (!prev) return prev
        const updated = { ...prev, avatar }
        localStorage.setItem(USER_KEY, JSON.stringify(updated))
        return updated
      })
    }
    window.addEventListener('vc-avatar-updated', handleAvatarUpdated)
    return () => window.removeEventListener('vc-avatar-updated', handleAvatarUpdated)
  }, [])

  const updateToken = useCallback((newToken) => {
    try {
      const payload = JSON.parse(atob(newToken.split('.')[1]))
      const newUser = { ...user, email: payload.email ?? user?.email }
      localStorage.setItem(TOKEN_KEY, newToken)
      localStorage.setItem(USER_KEY, JSON.stringify(newUser))
      setToken(newToken)
      setUser(newUser)
    } catch {}
  }, [user])

  return { token, user, login, logout, updateToken, error, loading, isAuthed: !!token }
}

// Global fetch wrapper: auto-logout on 401 from any API call
export function authFetch(token, url, opts = {}) {
  return fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
}

export async function uploadSession(token, session, readings) {
  const res = await authFetch(token, `${API}/sessions`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ session, readings }),
  })
  if (!res.ok) {
    const d = await res.json().catch(() => ({}))
    throw new Error(d.error || `HTTP ${res.status}`)
  }
  return res.json().catch(() => ({ ok: true }))
}

export async function uploadSessionCheckpoint(token, { sessionId, isFirst, isFinal, session, readings }) {
  const res = await authFetch(token, `${API}/sessions/checkpoint`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ sessionId, isFirst, isFinal, session, readings }),
  })
  if (!res.ok) {
    const d = await res.json().catch(() => ({}))
    throw new Error(d.error || `HTTP ${res.status}`)
  }
  return res.json().catch(() => ({ ok: true }))
}

export async function uploadAccRecording(token, sessionId, accPayload) {
  if (!sessionId || !accPayload) return null
  const res = await authFetch(token, `${API}/sessions/${encodeURIComponent(sessionId)}/acc`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(accPayload),
  })
  return res.ok ? res.json().catch(() => ({ ok: true })) : null
}

export async function fetchAccRecording(token, sessionId) {
  if (!sessionId) return null
  const res = await authFetch(token, `${API}/sessions/${encodeURIComponent(sessionId)}/acc`)
  if (!res.ok) return null
  return res.json().catch(() => null)
}
